import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Toaster } from 'react-hot-toast';
import 'react-toastify/dist/ReactToastify.css';
import Login from './components/auth/Login';
import SuperAdminLogin from './components/auth/SuperAdminLogin';
import ForgotPassword from './components/auth/ForgotPassword';
import { ForgotPasswordProvider } from './context/ForgotPasswordContext';
import ResetPassword from './components/auth/ResetPassword';
import { ResetPasswordProvider } from './context/ResetPasswordContext';
import Dashboard from './components/dashboard/Dashboard';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoadingProvider } from './context/LoadingContext';
import GuestRoute from './components/auth/GuestRoute';
import AuthRoute from './components/auth/AuthRoute';
import { loadAllScripts } from './utils/externalScripts';
import AdminDashboard from './components/admin/AdminDashboard';
import useRouteChange from './hooks/useRouteChange';
import ErrorBoundary from './components/common/ErrorBoundary';
import CustomerForm from './components/customers/CustomerForm';
import DashboardLayout from './components/layouts/DashboardLayout';
import { CustomerProvider } from './context/CustomerContext';
import { JobProvider } from './context/JobContext';
import { EstimationProvider } from './context/EstimationContext';
import CustomerList from './components/customers/CustomerList';

import JobList from './components/jobs/JobList';
import JobForm from './components/jobs/JobForm';
import EstimationList from './components/estimations/EstimationList';
import EstimationForm from './components/estimations/EstimationForm';
import EstimationView from './components/estimations/EstimationView';

import Profile from './components/profile/Profile';
import ChangePassword from './components/auth/ChangePassword';
import { ChangePasswordProvider } from './context/ChangePasswordContext';
import ProjectsList from './components/projects/ProjectsList';
import ProjectForm from './components/projects/ProjectForm';
import { ProjectProvider } from './context/ProjectContext';
import ServiceCategoriesList from './components/service-categories/ServiceCategoriesList';
import ServiceCategoryForm from './components/service-categories/ServiceCategoryForm';
import { ServiceCategoryProvider } from './context/ServiceCategoryContext';
import { ServiceProvider } from './context/ServiceContext';
import ServiceList from './components/services/ServiceList';
import ServiceForm from './components/services/ServiceForm';
import { ProductCategoryProvider } from './context/ProductCategoryContext';
import ProductCategoryList from './components/product-categories/ProductCategoryList';
import ProductCategoryForm from './components/product-categories/ProductCategoryForm';
import { EquipmentManufacturerProvider } from './context/EquipmentManufacturerContext';
import EquipmentManufacturerList from './components/equipment-manufacturers/EquipmentManufacturerList';
import EquipmentManufacturerForm from './components/equipment-manufacturers/EquipmentManufacturerForm';
import { EquipmentTypeProvider } from './context/EquipmentTypeContext';
import EquipmentTypeList from './components/equipment-types/EquipmentTypeList';
import EquipmentTypeForm from './components/equipment-types/EquipmentTypeForm';
import { ProductProvider } from './context/ProductContext';
import ProductList from './components/products/ProductList';
import ProductForm from './components/products/ProductForm';
import VendorList from './components/vendors/VendorList'; 
import VendorForm from './components/vendors/VendorForm'; 
import { VendorProvider } from './context/VendorContext';
import ExpenseCategoryList from './components/expenses-categories/ExpenseCategoryList'; 
import ExpenseCategoryForm from './components/expenses-categories/ExpenseCategoryForm'; 
import { ExpenseCategoryProvider } from './context/ExpenseCategoryContext';
import JobCategoryList from './components/job-categories/JobCategoryList';
import JobCategoryForm from './components/job-categories/JobCategoryForm';
import { JobCategoryProvider } from './context/JobCategoryContext';
import TimeOffRequestTypeList from './components/time-off-request-types/TimeOffRequestTypeList';
import TimeOffRequestTypeForm from './components/time-off-request-types/TimeOffRequestTypeForm';
import { TimeOffRequestTypeProvider } from './context/TimeOffRequestTypeContext';
import { PaymentMethodProvider } from './context/PaymentMethodContext';
import PaymentMethodList from './components/payment-methods/PaymentMethodList';
import PaymentMethodForm from './components/payment-methods/PaymentMethodForm';
import PaymentTermList from './components/payment-terms/PaymentTermList';
import PaymentTermForm from './components/payment-terms/PaymentTermForm';
import { PaymentTermProvider } from './context/PaymentTermContext';
import CustomerTagList from './components/customer-tags/CustomerTagList';
import CustomerTagForm from './components/customer-tags/CustomerTagForm';
import { CustomerTagProvider } from './context/CustomerTagContext';
import EstimateJobTagList from './components/estimate-job-tags/EstimateJobTagList';
import { EstimateJobTagProvider } from './context/EstimateJobTagContext';
import EstimateJobTagForm from './components/estimate-job-tags/EstimateJobTagForm';
import CompanyInfo from './components/company-info/CompanyInfo';
import { CompanyInfoProvider } from './context/CompanyInfoContext';
import CompanyInfoForm from './components/company-info/CompanyInfoForm';
import { CommunicationTemplatesProvider } from './context/CommunicationTemplatesContext';
import CommunicationTemplates from './components/communication-templates/CommunicationTemplates';
import CommunicationTemplateForm from './components/communication-templates/CommunicationTemplateForm';
import CompanyWorkForce from './components/company-workforce/CompanyWorkForce';
import CompanyWorkForceForm from './components/company-workforce/CompanyWorkForceForm';
import { CompanyWorkForceProvider } from './context/CompanyWorkForceContext';
import MyCalendar from './components/calendar/Calendar';
import TaskForm from './components/tasks/TaskForm';
import TaskList from './components/tasks/TaskList'
import { TaskProvider } from './context/TaskContext';
import ReportDashboard from './components/reports/ReportDashboard'
import { ErrorProvider } from './context/ErrorContext';
import useAutoLogoutOnIdle from './hooks/useAutoLogoutOnIdle';
import NotFound from './components/common/NotFound';
import { CountryProvider, useCountry } from './context/CountryContext';
import { RoleProvider } from './context/RoleContext';
import RoleList from './components/role/RoleList';
import RoleForm from './components/role/RoleForm';
import $ from 'jquery';
import 'jquery-ui/ui/widgets/datepicker';
import 'jquery-ui/themes/base/all.css'; // Optional but adds default styling
import { useForm } from 'react-hook-form';
import { PaymentTypeProvider } from './context/PaymentTypeContext';

const adminPrefix = process.env.REACT_APP_ADMIN_ROUTE_PREFIX || 'admin';
// Protected Route component with role-based access control
const ProtectedRoute = ({ children, adminOnly = false }) => {
 
  const { user } = useAuth();
  const location = useLocation();

  if (!user) {
   
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // For admin routes, check if user is admin
  if (adminOnly && user.role_id !== 1) {
    
    return <Navigate to="/dashboard" replace />;
  }
  
  // For non-admin routes, if user is admin and trying to access regular routes,
  // redirect them to the admin version of the route
  if (!adminOnly && user.role_id === 1 && !location.pathname.startsWith('/'+adminPrefix)) {
    const adminPath = `${adminPrefix}${location.pathname}`;
    
    return <Navigate to={adminPath} replace />;
  }

  // For non-admin users trying to access admin routes
  if (user.role_id !== 1 && location.pathname.startsWith('/'+adminPrefix)) {
    
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// AppRoutes component to use the route change hook
const AppRoutes = () => {
  const { user } = useAuth();
   
  useRouteChange();
  useAutoLogoutOnIdle();
  
  if (!user) {
   
    return (
      <Routes  element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
        <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
        <Route path="/reset-password" element={<GuestRoute><ResetPassword /></GuestRoute>} />
        <Route path={`${adminPrefix}/login`} element={<GuestRoute><SuperAdminLogin /></GuestRoute>} />
        <Route path={`${adminPrefix}`} element={<GuestRoute><SuperAdminLogin /></GuestRoute>} />
        <Route path="*"  element={<NotFound />}/>
        <Route path="/dashboard" element={<AuthRoute><Dashboard /></AuthRoute>} />
      </Routes> 
    );
  }

  return (
    <Routes>
      {/* Regular user routes */}
      <Route path="*" element={<NotFound />} />
      <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/customers" element={<CustomerList />} />
        <Route path="/customers/new" element={<CustomerForm />} />
        <Route path="/customers/edit/:id" element={<CustomerForm />} />
        <Route path="/jobs" element={<JobList />} />
        <Route path="/jobs/new" element={<JobForm />} />
        <Route path="/jobs/edit/:id" element={<JobForm />} />
        <Route path="/estimations" element={<EstimationList />} />
        <Route path="/estimations/new" element={<EstimationForm />} />
        <Route path="/estimations/edit/:id" element={<EstimationForm />} />
        <Route path="/estimations/view/:id" element={<EstimationView />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/my-office" element={<div>My Office Page</div>} />
        <Route path="/projects" element={<ProjectsList />} />
        <Route path="/projects/create" element={<ProjectForm />} />
        <Route path="/projects/edit/:id" element={<ProjectForm />} />
        <Route path="/estimates" element={<div>Estimates Page</div>} />
        <Route path="/dispatch" element={<div>Dispatch Page</div>} />
        <Route path="/accounting" element={<div>Accounting Page</div>} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/service-categories" element={<ServiceCategoriesList />} />
        <Route path="/service-categories/create" element={<ServiceCategoryForm />} />
        <Route path="/service-categories/edit/:id" element={<ServiceCategoryForm />} />
        <Route path="/services" element={<ServiceList />} />
        <Route path="/services/new" element={<ServiceForm />} />
        <Route path="/services/edit/:id" element={<ServiceForm />} />
        <Route path="/product-categories" element={<ProductCategoryList />} />
        <Route path="/product-categories/create" element={<ProductCategoryForm />} />
        <Route path="/product-categories/edit/:id" element={<ProductCategoryForm />} />
        <Route path="/equipment-manufacturers" element={<EquipmentManufacturerList />} />
        <Route path="/equipment-manufacturers/create" element={<EquipmentManufacturerForm />} />
        <Route path="/equipment-manufacturers/edit/:id" element={<EquipmentManufacturerForm />} />
        <Route path="/equipment-types" element={<EquipmentTypeList />} />
        <Route path="/equipment-types/create" element={<EquipmentTypeForm />} />
        <Route path="/equipment-types/edit/:id" element={<EquipmentTypeForm />} />
        <Route path="/products" element={<ProductList />} />
        <Route path="/products/create" element={<ProductForm />} />
        <Route path="/products/edit/:id" element={<ProductForm />} />
        <Route path="/vendors" element={<VendorList />} />
        <Route path="/vendors/create" element={<VendorForm />} />
        <Route path="/vendors/edit/:id" element={<VendorForm />} />
        <Route path="/expense-categories" element={<ExpenseCategoryList />} />
        <Route path="/expense-categories/create" element={<ExpenseCategoryForm />} />
        <Route path="/expense-categories/edit/:id" element={<ExpenseCategoryForm />} />
        <Route path="/job-categories" element={<JobCategoryList />} />
        <Route path="/job-categories/create" element={<JobCategoryForm />} />
        <Route path="/job-categories/edit/:id" element={<JobCategoryForm />} />
        <Route path="/time-off-request-types" element={<TimeOffRequestTypeList />} />
        <Route path="/time-off-request-types/create" element={<TimeOffRequestTypeForm />} />
        <Route path="/time-off-request-types/edit/:id" element={<TimeOffRequestTypeForm />} />
        <Route path="/payment-methods" element={<PaymentMethodList />} />
        <Route path="/payment-methods/create" element={<PaymentMethodForm />} />
        <Route path="/payment-methods/edit/:id" element={<PaymentMethodForm />} />
        <Route path="/payment-terms" element={<PaymentTermList />} />
        <Route path="/payment-terms/create" element={<PaymentTermForm />} />
        <Route path="/payment-terms/edit/:id" element={<PaymentTermForm />} />
        <Route path="/customer-tags" element={<CustomerTagList />} />
        <Route path="/customer-tags/create" element={<CustomerTagForm />} />
        <Route path="/customer-tags/edit/:id" element={<CustomerTagForm />} />
        <Route path="/estimate-job-tags" element={<EstimateJobTagList />} />
        <Route path="/estimate-job-tags/create" element={<EstimateJobTagForm />} />
        <Route path="/estimate-job-tags/edit/:id" element={<EstimateJobTagForm />} />
        <Route path="/company-info" element={<CompanyInfo />} />
        <Route path="/company-info/create" element={<CompanyInfoForm />} />
        <Route path="/company-info/edit/:id" element={<CompanyInfoForm />} />
        <Route path="/communication-templates" element={<CommunicationTemplates />} />
        <Route path="/communication-templates/create" element={<CommunicationTemplateForm />} />
        <Route path="/communication-templates/edit/:id" element={<CommunicationTemplateForm />} />
        <Route path="/company-workforces" element={<CompanyWorkForce />} />
        <Route path="/company-workforces/create" element={<CompanyWorkForceForm />} />
        <Route path="/company-workforces/edit/:id" element={<CompanyWorkForceForm />} />
        <Route path="/calendar" element={<MyCalendar />} /> 
        <Route path="/tasks" element={<TaskList />} />
        <Route path="/tasks/new" element={<TaskForm />} /> 
        <Route path="/tasks/edit/:id" element={<TaskForm />} /> 
        <Route path="/reports" element={<ReportDashboard />} />
       
      </Route>

      {/* Admin routes */}
      <Route path={`${adminPrefix}/`} element={<ProtectedRoute adminOnly><DashboardLayout /></ProtectedRoute>}>
       
        <Route path="login" element={<GuestRoute><SuperAdminLogin /></GuestRoute>} />
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="customers" element={<CustomerList />} />
        <Route path="customers/new" element={<CustomerForm />} />
        <Route path="customers/edit/:id" element={<CustomerForm />} />
        <Route path="jobs" element={<JobList />} />
        <Route path="jobs/new" element={<JobForm />} />
        <Route path="jobs/edit/:id" element={<JobForm />} />
        <Route path="estimations" element={<EstimationList />} />
        <Route path="estimations/new" element={<EstimationForm />} />
        <Route path="estimations/edit/:id" element={<EstimationForm />} />
        <Route path="estimations/view/:id" element={<EstimationView />} />
        <Route path="profile" element={<Profile />} />
        <Route path="change-password" element={<ChangePassword />} />
        <Route path="projects" element={<ProjectsList />} />
        <Route path="projects/create" element={<ProjectForm />} />
        <Route path="projects/edit/:id" element={<ProjectForm />} />
        <Route path="service-categories" element={<ServiceCategoriesList />} />
        <Route path="service-categories/create" element={<ServiceCategoryForm />} />
        <Route path="service-categories/edit/:id" element={<ServiceCategoryForm />} />
        <Route path="services" element={<ServiceList />} />
        <Route path="services/new" element={<ServiceForm />} />
        <Route path="services/edit/:id" element={<ServiceForm />} />
        <Route path="product-categories" element={<ProductCategoryList />} />
        <Route path="product-categories/create" element={<ProductCategoryForm />} />
        <Route path="product-categories/edit/:id" element={<ProductCategoryForm />} />
        <Route path="equipment-manufacturers" element={<EquipmentManufacturerList />} />
        <Route path="equipment-manufacturers/create" element={<EquipmentManufacturerForm />} />
        <Route path="equipment-manufacturers/edit/:id" element={<EquipmentManufacturerForm />} />
        <Route path="equipment-types" element={<EquipmentTypeList />} />
        <Route path="equipment-types/create" element={<EquipmentTypeForm />} />
        <Route path="equipment-types/edit/:id" element={<EquipmentTypeForm />} />
        <Route path="products" element={<ProductList />} />
        <Route path="products/create" element={<ProductForm />} />
        <Route path="products/edit/:id" element={<ProductForm />} />
        <Route path="vendors" element={<VendorList />} />
        <Route path="vendors/create" element={<VendorForm />} />
        <Route path="vendors/edit/:id" element={<VendorForm />} />
        <Route path="expense-categories" element={<ExpenseCategoryList />} />
        <Route path="expense-categories/create" element={<ExpenseCategoryForm />} />
        <Route path="expense-categories/edit/:id" element={<ExpenseCategoryForm />} />
        <Route path="job-categories" element={<JobCategoryList />} />
        <Route path="job-categories/create" element={<JobCategoryForm />} />
        <Route path="job-categories/edit/:id" element={<JobCategoryForm />} />
        <Route path="time-off-request-types" element={<TimeOffRequestTypeList />} />
        <Route path="time-off-request-types/create" element={<TimeOffRequestTypeForm />} />
        <Route path="time-off-request-types/edit/:id" element={<TimeOffRequestTypeForm />} /> 
        <Route path="payment-methods" element={<PaymentMethodList />} />    
        <Route path="payment-methods/create" element={<PaymentMethodForm />} />
        <Route path="payment-methods/edit/:id" element={<PaymentMethodForm />} />
        <Route path="payment-terms" element={<PaymentTermList />} />
        <Route path="payment-terms/create" element={<PaymentTermForm />} />
        <Route path="payment-terms/edit/:id" element={<PaymentTermForm />} />
        <Route path="customer-tags" element={<CustomerTagList />} />
        <Route path="customer-tags/create" element={<CustomerTagForm />} />
        <Route path="customer-tags/edit/:id" element={<CustomerTagForm />} />
        <Route path="estimate-job-tags" element={<EstimateJobTagList />} />
        <Route path="estimate-job-tags/create" element={<EstimateJobTagForm />} />
        <Route path="estimate-job-tags/edit/:id" element={<EstimateJobTagForm />} />
        <Route path="company-info" element={<CompanyInfo />} />
        <Route path="company-info/create" element={<CompanyInfoForm />} />
        <Route path="company-info/edit/:id" element={<CompanyInfoForm />} />
        <Route path="communication-templates" element={<CommunicationTemplates />} />
        <Route path="communication-templates/create" element={<CommunicationTemplateForm />} />
        <Route path="communication-templates/edit/:id" element={<CommunicationTemplateForm />} />
        <Route path="company-workforces" element={<CompanyWorkForce />} />
        <Route path="company-workforces/create" element={<CompanyWorkForceForm />} />
        <Route path="company-workforces/edit/:id" element={<CompanyWorkForceForm />} />
        <Route path="calendar" element={<MyCalendar />} /> 
        <Route path="tasks" element={<TaskList />} />
        <Route path="tasks/new" element={<TaskForm />} /> 
        <Route path="tasks/edit/:id" element={<TaskForm />} /> 
        <Route path="reports" element={<ReportDashboard />} />
        <Route path="roles" element={<RoleList />} />
        <Route path="roles/create" element={<RoleForm />} />
        <Route path="roles/edit/:id" element={<RoleForm />} />
        
      </Route>
    </Routes>
  );
};


// Add this new component
const CountryInitializer = () => {
  const { countries, getCountries, isApiReady } = useCountry();
  const initialized = React.useRef(false);

  useEffect(() => {
    if (isApiReady && !initialized.current && countries.length === 0) {
      initialized.current = true;
      getCountries();
    }
  }, [isApiReady, countries.length, getCountries]);

  return null;
};
 
 

function App() {
  
  const { setValue } = useForm();
 
  useEffect(() => {
    $(document).on('focus', '.datepicker', function () {
      if (!$(this).hasClass('hasDatepicker')) {
        $(this).datepicker({
          dateFormat: 'mm-dd-yy',
          onSelect: function (dateText) {
            // ✅ Set value to input
            this.value = dateText;

            // ✅ Create a real React-compatible input event
            const inputEvent = new Event('input', { bubbles: true });
            this.dispatchEvent(inputEvent);
          },
        });
      }
    });

    return () => {
      $(document).off('focus', '.datepicker');
    };
  }, [setValue]);

  useEffect(() => {
     
    const initializeScripts = async () => {
      try {
        // Wait for DOM to be fully loaded
        if (document.readyState === 'loading') {
          await new Promise(resolve => {
            document.addEventListener('DOMContentLoaded', resolve);
          });
        }
        
        // Add a small delay to ensure all React components are mounted
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Load external scripts
        await loadAllScripts();
        
      } catch (error) {
        console.error('Error loading scripts:', error);
        // Don't throw the error, just log it
      }
    };

    initializeScripts();
  }, [ ]);

  return (
    <Router>
      <ErrorBoundary>
        <LoadingProvider>
          <AuthProvider>
            <ErrorProvider>
              <CountryProvider>
                <CountryInitializer />
                <PaymentTypeProvider>
                <ProjectProvider>
                <ServiceCategoryProvider>
                <EquipmentManufacturerProvider>
                <EquipmentTypeProvider>
                <ChangePasswordProvider>
                  <ResetPasswordProvider>
                    <ForgotPasswordProvider>
                      <TaskProvider>
                        <CustomerProvider>
                          <JobProvider>
                            <CompanyWorkForceProvider>
                              <CompanyInfoProvider>
                                <CommunicationTemplatesProvider>
                                  <EstimateJobTagProvider>
                                    <CustomerTagProvider>
                                      <PaymentTermProvider>
                                        <PaymentMethodProvider>
                                          <TimeOffRequestTypeProvider>
                                            <EstimationProvider>
                                              <ServiceProvider>
                                                <ProductCategoryProvider>
                                                  <ProductProvider>
                                                    <VendorProvider>
                                                      <ExpenseCategoryProvider>
                                                        <JobCategoryProvider>
                                                        <RoleProvider>
                                                          <AppRoutes />
                                                          <ToastContainer />
                                                          <Toaster position="top-right" />
                                                        </RoleProvider>
                                                                                                              </JobCategoryProvider>
                                                      </ExpenseCategoryProvider>
                                                    </VendorProvider>
                                                  </ProductProvider>
                                                </ProductCategoryProvider>
                                              </ServiceProvider>
                                            </EstimationProvider>
                                          </TimeOffRequestTypeProvider>
                                        </PaymentMethodProvider>
                                      </PaymentTermProvider>
                                    </CustomerTagProvider>
                                  </EstimateJobTagProvider>
                                </CommunicationTemplatesProvider>
                              </CompanyInfoProvider>
                            </CompanyWorkForceProvider>
                          </JobProvider>
                        </CustomerProvider>
                      </TaskProvider>
                    </ForgotPasswordProvider>
                  </ResetPasswordProvider>
                </ChangePasswordProvider>
                </EquipmentTypeProvider>
                </EquipmentManufacturerProvider>
                </ServiceCategoryProvider>
                </ProjectProvider>
                </PaymentTypeProvider>
              </CountryProvider>
            </ErrorProvider>
          </AuthProvider>
        </LoadingProvider>
      </ErrorBoundary>
    </Router>
  );
}

export default App; 